import { createFileRoute, Outlet } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { customDashboardsSearchSchema } from '@/features/custom-dashboards/search'
import { usePinnedDashboardsSync } from '@/features/custom-dashboards/use-pinned-dashboards'

import { ROLES } from '@/shared/auth/constants'
import { roleAuthGuard } from '@/shared/auth/utils/router'

/**
 * Custom Dashboards surface — LAYOUT route. Owns the shared concerns for the whole
 * surface (search-param validation + role gate) and renders an <Outlet/> for its two
 * children: the dashboard index (`.index`) and the full-page Widget Builder (`.builder`).
 *
 * READ is every role — every endpoint this surface reads from (`/bql`, `/bql/schema`,
 * `/bql/fields`, `/bql/values`, and the dashboards config) is within Read Only's grant
 * — so this gate lets everyone in and the write affordances inside are gated one by one. The permission table, and why the
 * boundary is role-based rather than permission-based, are written out ONCE in
 * `useCanManageDashboards`; that hook is also the one place to change if a per-permission
 * gate ever lands.
 *
 * The `.builder` child re-gates at Analyst: authoring a widget is a write end to end,
 * so a Read Only deep link there lands on /unauthorized, which is honest.
 */
export const Route = createFileRoute('/_dashboard/custom-dashboards')({
	validateSearch: zodValidator(customDashboardsSearchSchema),
	beforeLoad: () => {
		// A no-op against today's four roles — kept because it is the greppable
		// declaration of where the READ boundary sits, and its absence would read
		// as "this surface has no gate" rather than "its gate is the floor".
		roleAuthGuard(ROLES.ReadOnly)
	},
	head: () => ({ meta: [{ title: 'Custom Dashboards | BitLyft AIR®' }] }),
	component: CustomDashboardsLayout,
	errorComponent: RouteErrorComponent,
})

/**
 * The layout renders nothing of its own; it exists here to host the ONE mount of
 * the pin sync (ticket 42). Every surface that can pin — the index's switcher,
 * the Library's Dashboards tab — is a descendant, and the layout stays mounted
 * across navigation between them, so the debounced write is never cut in half by
 * a route change inside the surface. A second mount anywhere would be a second
 * independently-debounced deep merge into one `unsafeMetadata` key.
 *
 * Read-only consumers (the app sidebar, which renders on every route) call
 * `usePinnedDashboardIds()` and mount no writer.
 */
function CustomDashboardsLayout() {
	usePinnedDashboardsSync()
	return <Outlet />
}
